import { Switch, FormControlLabel, FormGroup, styled, Checkbox, Link } from '@mui/material'
import {
  CsvConfig,
  CsvConfigInvoice,
  CsvConfigOcrBuilder,
  getCsvConfigInvoice,
  getCsvConfigOrderForm,
  saveCsvConfig,
  getCsvConfigOcrBuilder,
  CsvConfigOrderForm,
} from 'api/csv'
import { CategoryOpt, ChildOpt } from 'components/CsvConfigDialog/common'
import TwoSideMultiSelector from 'components/CsvConfigDialog/TwoSideMultiSelector'
import DialogBase from 'components/dialog/Base'
import { DOC_TYPE, DocType, ERROR_TEXT, DELIM } from 'config'
import { OCR_RESP_INVOICE_KEYS } from 'entities/DocumentsInvoiceV2'
import { OCR_RESP_ORDER_FORM_KEYS } from 'entities/DocumentsOrderForm'
import { useAlert } from 'hooks/useDialog'
import { useState, useEffect, useCallback } from 'react'

type Props = {
  docType: DocType
  docTypeName: string
  subGroup?: string
  open: boolean
  actionText?: string
  ocrBuilderConfigID?: number
  onClose: (canceled: boolean) => void
}

interface OcrBuilderChildOpt extends ChildOpt {
  orgKey: string
  dispOrder: number
}

const MSG_CONFIG_NOT_FOUND = [
  '現在の連携設定では全ての項目が出力対象となっています。',
  '新しく連携を行うと出力項目選択が可能です。',
].join('\n')

const ConfigNotFoundDiv = styled('div')(({ theme }) => ({
  fontSize: '1.1rem',
  color: theme.palette.error.dark,
  position: 'absolute',
  textAlign: 'left',
  padding: '4rem 60% 0 1.4rem', // paddingtopはbase dialogにてoverwriteされる
  height: '70%',
  zIndex: 1,
}))

const compF = (a, b) => a.csvOrder - b.csvOrder

function convertInvoice2Opts(data: CsvConfigInvoice): CategoryOpt[] {
  const base = [[data.invoice.fileName]]
    .concat(
      OCR_RESP_INVOICE_KEYS.filter((s) => s !== 'itemLines').map((k) => data.invoice[k].columns),
    )
    .flat()
    .sort(compF)

  const itemLines = data.invoice.itemLines.columns.sort(compF)

  const categories = [
    {
      key: 'base',
      name: '基本項目',
      items: base.map(
        // key ex> headerBase.invoceDate --> base.headerBase.invoceDate
        ({ key, name, output }) => ({ key: `base${DELIM}${key}`, name, picked: output }),
      ),
    },
    {
      key: 'items',
      name: '品目',
      items: itemLines.map(
        // key ex> itemLines.item1 --> items.itemLines.item1
        ({ key, name, output }) => ({ key: `items${DELIM}${key}`, name, picked: output }),
      ),
    },
  ]
  return categories
}

function convertOrderForm2Opts(data: CsvConfigOrderForm): CategoryOpt[] {
  const base = [[data.orderForm.fileName]]
    .concat(
      OCR_RESP_ORDER_FORM_KEYS.filter((s) => s !== 'itemLines').map(
        (k) => data.orderForm[k].columns,
      ),
    )
    .flat()
    .sort(compF)

  const itemLines = data.orderForm.itemLines.columns.sort(compF)

  const categories = [
    {
      key: 'base',
      name: '基本項目',
      items: base.map(
        // key ex> headerBase.invoceDate --> base.headerBase.invoceDate
        ({ key, name, output }) => ({ key: `base${DELIM}${key}`, name, picked: output }),
      ),
    },
    {
      key: 'items',
      name: '品目',
      items: itemLines.map(
        // key ex> itemLines.item1 --> items.itemLines.item1
        ({ key, name, output }) => ({ key: `items${DELIM}${key}`, name, picked: output }),
      ),
    },
  ]
  return categories
}

function convertOpts2Invoice(opts: CategoryOpt[]) {
  const rtn = OCR_RESP_INVOICE_KEYS.reduce(
    (prev, key) => {
      prev[key] = { columns: [] }
      return prev
    },
    {} as CsvConfigInvoice['invoice'],
  )

  opts.forEach((g) => {
    let csvOrder = 0
    g.items.forEach((o) => {
      // key ex> base:headerBase.invoceDate --> headerBase.invoiceDate
      const [_optCate, key] = o.key.split(DELIM)
      // key ex> headerBase.invoceDate --> headerBase, invoiceDate
      const [invoiceCate, _subKey] = key.split('.')
      // key examples>
      //   invoiceCate: headerBase (BE CAREFUL: fileName is a special case)
      //   key:         headerBase.invoiceDate
      if (invoiceCate === 'fileName') {
        rtn.fileName = { key, name: o.name, output: o.picked, csvOrder }
      } else {
        rtn[invoiceCate].columns.push({ key, name: o.name, output: o.picked, csvOrder })
      }
      csvOrder++
    })
  })
  return rtn
}

function convertOpts2OrderForm(opts: CategoryOpt[]) {
  const rtn = OCR_RESP_ORDER_FORM_KEYS.reduce(
    (prev, key) => {
      prev[key] = { columns: [] }
      return prev
    },
    {} as CsvConfigOrderForm['orderForm'],
  )

  opts.forEach((g) => {
    let csvOrder = 0
    g.items.forEach((o) => {
      // key ex> base:headerBase.orderFormDate --> headerBase.orderFormDate
      const [_optCate, key] = o.key.split(DELIM)
      // key ex> headerBase.orderFormDate --> headerBase, orderFormDate
      const [cate, _subKey] = key.split('.')
      // key examples>
      //   invoiceCate: headerBase (BE CAREFUL: fileName is a special case)
      //   key:         headerBase.invoiceDate
      if (cate === 'fileName') {
        rtn.fileName = { key, name: o.name, output: o.picked, csvOrder }
      } else {
        rtn[cate].columns.push({ key, name: o.name, output: o.picked, csvOrder })
      }
      csvOrder++
    })
  })
  return rtn
}

function convertOcrBuilder2Opts(data: CsvConfigOcrBuilder) {
  return data.ocrBuilder.config.sort(compF).map(({ no, templateName, columns }) => ({
    key: no.toString(),
    name: templateName,
    items: columns.sort(compF).map(
      ({ key, name, output, dispOrder }, colIdx): ChildOpt => ({
        key: `${no}${DELIM}${key}${DELIM}${colIdx}`, // colIdx: key can be duplicate?
        orgKey: key,
        name,
        dispOrder,
        picked: output,
      }),
    ),
  }))
}

function convertOpts2OcrBuilder(opts: CategoryOpt[]) {
  let templateCsvOder = 0
  return {
    config: opts.map((g) => {
      let csvOrder = 0
      return {
        no: parseInt(g.key),
        templateName: g.name,
        templateCsvOrder: templateCsvOder++,
        columns: (g.items as OcrBuilderChildOpt[]).map((i) => ({
          dispOrder: i.dispOrder,
          key: i.orgKey,
          name: i.name,
          output: i.picked,
          csvOrder: csvOrder++,
        })),
      }
    }),
  }
}

const CheckboxSettings = ({
  displayDialog,
  setDisplayDialog,
  disabled,
}: {
  displayDialog: boolean
  setDisplayDialog: React.Dispatch<React.SetStateAction<boolean>>
  disabled: boolean
}) => {
  return (
    <FormControlLabel
      label="出力時の項目選択表示"
      style={{ marginLeft: 0 }}
      control={
        <Checkbox
          size="small"
          color="primary"
          checked={displayDialog}
          onChange={({ target }) => {
            setDisplayDialog(target.checked)
          }}
          disabled={disabled}
          data-testid="output-item"
        />
      }
    />
  )
}

const CheckboxDownload = ({
  displayDialog,
  setDisplayDialog,
  disabled,
}: {
  displayDialog: boolean
  setDisplayDialog: React.Dispatch<React.SetStateAction<boolean>>
  disabled: boolean
}) => {
  return (
    <span>
      <Checkbox
        size="small"
        color="primary"
        checked={!displayDialog}
        onChange={() => setDisplayDialog((f) => !f)}
        disabled={disabled}
        data-testid="dont-show-again-checkbox"
      />
      <Link
        align="left"
        color="inherit"
        underline="none"
        component="button"
        onClick={() => setDisplayDialog((f) => !f)}
        disabled={disabled}
        data-testid="dont-show-again-button"
      >
        {' '}
        今後このダイアログボックスを表示しない
        <br />
        設定画面より再度設定することが可能です
      </Link>
    </span>
  )
}

const Options = ({
  type,
  printHeader,
  displayDialog,
  setPrintHeader,
  setDisplayDialog,
  disabled,
}: {
  type: 'download' | 'setting'
  printHeader: boolean
  displayDialog: boolean
  setPrintHeader: React.Dispatch<React.SetStateAction<boolean>>
  setDisplayDialog: React.Dispatch<React.SetStateAction<boolean>>
  disabled: boolean
}) => {
  const CheckBoxDisplay = type === 'download' ? CheckboxDownload : CheckboxSettings

  return (
    <FormGroup
      row
      style={{
        justifyContent: 'space-between',
        margin: '10px 90px 0 10px',
      }}
    >
      <CheckBoxDisplay
        displayDialog={displayDialog}
        setDisplayDialog={setDisplayDialog}
        disabled={disabled}
      />
      <FormControlLabel
        label="Header出力"
        control={
          <Switch
            color="primary"
            checked={printHeader}
            onChange={({ target }) => {
              setPrintHeader(target.checked)
            }}
            disabled={disabled}
            data-testid="print-header"
          />
        }
      />
    </FormGroup>
  )
}

const InvoiceOcrBuilderCsvConfigDialog = ({
  docType,
  docTypeName,
  open,
  ocrBuilderConfigID,
  actionText,
  onClose,
  subGroup,
}: Props) => {
  const alert = useAlert()
  const readerHelpIconProps = {
    builderPath: '/builder/ja-JP/ocr-reader-doc/document-list.html#CsvItems',
    concurPath: '/concur/ja-JP/ocr-reader-doc/document-list.html#CsvItems',
    invoicePath: '/invoice/ja-JP/ocr-reader-doc/document-list.html#CsvItems',
    orderFormPath: '/order-form/ja-JP/ocr-reader-doc/document-list.html#CsvItems',
  }

  // const [loading, setLoading] = useState(false)
  const [csvConfig, setCsvConfig] = useState<CsvConfig | null>(null)
  const [optsState, setOptsState] = useState<CategoryOpt[] | null>(null)
  const [configNotFound, setConfigNotFound] = useState(false)
  const [displayDialog, setDisplayDialg] = useState(true)
  const [printHeader, setPrintHeader] = useState(true)

  const doClose = useCallback(
    (a: boolean) => {
      setCsvConfig(null)
      setOptsState(null)
      setConfigNotFound(false)
      return onClose(a)
    },
    [onClose],
  )

  const doGetCsvConfig = useCallback(async () => {
    try {
      if (docType === DOC_TYPE.OCR_BUILDER) {
        const data = (await getCsvConfigOcrBuilder(
          ocrBuilderConfigID!,
          subGroup,
        )) as CsvConfigOcrBuilder
        if (data === null) {
          // special case
          setConfigNotFound(true)
        } else {
          setCsvConfig(data)
          setOptsState(convertOcrBuilder2Opts(data))
          setDisplayDialg(data.dialogDisp)
          setPrintHeader(data.header)
        }
      } else if (docType === DOC_TYPE.ORDER_FORM) {
        const data = await getCsvConfigOrderForm(subGroup)
        setCsvConfig(data)
        setOptsState(convertOrderForm2Opts(data))
        setDisplayDialg(data.dialogDisp)
        setPrintHeader(data.header)
      } else {
        const data = await getCsvConfigInvoice(subGroup)
        setCsvConfig(data)
        setOptsState(convertInvoice2Opts(data))
        setDisplayDialg(data.dialogDisp)
        setPrintHeader(data.header)
      }
    } catch (err) {
      alert({ message: '設定取得に失敗しました' })
      doClose(true)
    }
  }, [docType, alert, ocrBuilderConfigID, doClose, subGroup])

  useEffect(() => {
    open && doGetCsvConfig()
  }, [open, doGetCsvConfig])

  const handleSave = async () => {
    if (configNotFound) return doClose(false)
    if (!optsState!.find((o) => o.items.find((i) => i.picked))) {
      return alert({ message: ERROR_TEXT.csvSelectZero })
    }
    try {
      const common = {
        dialogDisp: displayDialog,
        header: printHeader,
      }
      if (docType === DOC_TYPE.OCR_BUILDER) {
        await saveCsvConfig(docType, subGroup, ocrBuilderConfigID, {
          ...csvConfig!,
          ...common,
          ocrBuilder: convertOpts2OcrBuilder(optsState!),
        })
      } else if (docType === DOC_TYPE.ORDER_FORM) {
        await saveCsvConfig(docType, subGroup, undefined, {
          ...csvConfig!,
          ...common,
          orderForm: convertOpts2OrderForm(optsState!),
        })
      } else {
        await saveCsvConfig(docType, subGroup, undefined, {
          ...csvConfig!,
          ...common,
          invoice: convertOpts2Invoice(optsState!),
        })
      }
    } catch (err) {
      await alert({ message: '設定格納に失敗しました' })
      return doClose(true)
    }
    doClose(false)
  }

  // eslint-disable-next-line prefer-template
  const title = `出力設定: ${docTypeName}${subGroup === undefined ? '' : '(' + subGroup + ')'}`
  return (
    <DialogBase
      loading={!optsState && !configNotFound}
      open={open}
      title={title}
      readerHelpIconProps={readerHelpIconProps}
      onCancel={() => doClose(true)}
      onClose={() => doClose(true)}
      onAction={handleSave}
      actionText={actionText || '設定'}
      actionProps={{ disabled: configNotFound && !actionText }}
      cancelText="キャンセル"
    >
      {configNotFound && (
        <ConfigNotFoundDiv style={{ paddingTop: '4rem' }}>{MSG_CONFIG_NOT_FOUND}</ConfigNotFoundDiv>
      )}
      <TwoSideMultiSelector
        leftTitle="CSV出力しない項目"
        rightTitle="CSV出力する項目"
        optsState={optsState}
        setOptsState={setOptsState}
        showCateController={docType === DOC_TYPE.OCR_BUILDER}
      />
      <Options
        type={actionText ? 'download' : 'setting'}
        printHeader={printHeader}
        displayDialog={displayDialog}
        setPrintHeader={setPrintHeader}
        setDisplayDialog={setDisplayDialg}
        disabled={configNotFound}
      />
    </DialogBase>
  )
}

export default InvoiceOcrBuilderCsvConfigDialog
